// Events of the parking system (be.web), as the parking proxy / the PHP archive deliver them:
// {k, id, time, ts, type, gate, plate, kind, dir?, amount?, user?, ident?, alias?, dialog?}
// The raw `type` is be.web's English event name; everything here (label, category, severity) is ours.
import { ACCESS_TOKEN_KEY, BACKEND_URL, PARKING_IMG_URL } from '../config';
import { doSwaggerCall } from '../hooks/useApi';

export const SEVERITY = Object.freeze({ INFO: 'info', WARN: 'warn', FAIL: 'fail' });

export const CATEGORIES = Object.freeze({
    move: { label: 'Mozgás', color: '#2a78d6' },
    pay: { label: 'Fizetés', color: '#1e9e5a' },
    payst: { label: 'Automata', color: '#7a5af5' },
    reject: { label: 'Elutasítás', color: '#d32f2f' },
    error: { label: 'Jegy / rendszám hiba', color: '#ef6c00' },
    pass: { label: 'Bérlet', color: '#00897b' },
    discount: { label: 'Kedvezmény', color: '#c2185b' },
    capacity: { label: 'Telítettség', color: '#8d6e63' },
    device: { label: 'Sorompó / eszköz', color: '#546e7a' },
    operator: { label: 'Operátor', color: '#f9a825' },
    app: { label: 'App / backoffice', color: '#0288d1' },
    other: { label: 'Egyéb', color: '#9e9e9e' },
});

const T = (label, cat, sev = SEVERITY.INFO, extra = {}) => ({ label, cat, sev, ...extra });
const { INFO, WARN, FAIL } = SEVERITY;

// every type seen on the live system (2026-10), unknown ones fall back to "other"
export const EVENT_TYPES = Object.freeze({
    'ID moved': T('Áthaladás', 'move'),
    'Pay off': T('Kihajtás lekönyvelve', 'move', INFO, { noise: true }),
    'ID moving canceled': T('Áthaladás megszakadt', 'move', WARN),
    'ID paid': T('Fizetés', 'pay'),
    'No payment needed': T('Nem kell fizetni', 'payst'),
    'ID in free time': T('Szabad időn belül', 'payst'),
    'ID not paid': T('Automatánál: még nincs fizetve', 'payst'),
    'Interim report': T('Részjelentés', 'payst', INFO, { noise: true }),
    'Infeeder 2 is empty': T('Pénzadagoló üres', 'device', WARN),
    'ID rejected unpaid': T('Kijáratnál elutasítva: nincs fizetve', 'reject', FAIL),
    'ID park ID wrong': T('Ismeretlen parkolási azonosító', 'error', FAIL),
    'ID state wrong': T('Rossz jegyállapot', 'error', FAIL),
    'ID not in system': T('Nincs a rendszerben', 'error', FAIL),
    'ID has wrong media type': T('Rossz adathordozó', 'error', FAIL),
    'ID not payable': T('Nem fizethető', 'error', FAIL),
    'ID without barcode': T('Vonalkód nélküli jegy', 'error', WARN),
    'ID not readable': T('Olvashatatlan jegy', 'error', FAIL),
    'ID is in use at an other terminal': T('Másik terminálon használatban', 'error', WARN),
    'No allowed terminal in timezone of ID': T(
        'Ebben az időben itt nem engedélyezett',
        'error',
        FAIL
    ),
    'ID only function cards allowed (no presence loop?)': T(
        'Csak funkciókártya (nincs jármű a hurkon?)',
        'error',
        WARN
    ),
    'ID in APB mode': T('Antipassback: már bent van', 'error', FAIL),
    'Alias ticket used': T('Bérlet / alias jegy használva', 'pass'),
    'Discount code used': T('Kedvezmény beváltva', 'discount'),
    'Discount already applied': T('Kedvezmény már beváltva', 'discount', WARN),
    'Parker counter full': T('Parkoló tele', 'capacity', WARN),
    'Barrier opened by unknown source': T('Sorompó ismeretlen forrásból nyílt', 'device', FAIL),
    "Can't open": T('Sorompó nem nyílik', 'device', FAIL),
    'Device is offline': T('Eszköz offline', 'device', FAIL),
    'Device is online': T('Eszköz online', 'device'),
    'Safety loop is permanently touched': T(
        'Biztonsági hurok folyamatosan foglalt',
        'device',
        WARN
    ),
    'Safety loop was touched again': T('Biztonsági hurok újra foglalt', 'device', WARN),
    'Presence loop is permanently touched': T(
        'Jelenlét hurok folyamatosan foglalt',
        'device',
        WARN
    ),
    'Mode "Open"': T('Mód: nyitva', 'device', WARN),
    'Mode "Normal"': T('Mód: normál', 'device'),
    'Mode "Free exit"': T('Mód: szabad kihajtás', 'device', WARN),
    'Mode "Closed"': T('Mód: zárva', 'device', WARN),
    'Control "Manual"': T('Vezérlés: kézi', 'device', WARN),
    'Control "Automatic"': T('Vezérlés: automatikus', 'device'),
    'Mode: Open': T('Operátor: sorompó nyitva', 'operator', WARN),
    'Mode: Normal': T('Operátor: normál mód', 'operator'),
    'Mode: Free exit': T('Operátor: szabad kihajtás', 'operator', WARN),
    Edit: T('Operátor: szerkesztés', 'operator'),
    Delete: T('Operátor: törlés', 'operator', WARN),
    Add: T('Operátor: hozzáadás', 'operator'),
    Antipassback: T('Operátor: antipassback', 'operator'),
    View: T('Operátor: megtekintés', 'operator', INFO, { noise: true }),
    Sign: T('Operátor: nyugtázás', 'operator', INFO, { noise: true }),
    // ours: the parking proxy asks the fee (SOAP getTicketInfo) right after an exit rejection / manual opening
    'Ticket info': T('Díj lekérdezve', 'app'),
});

// gates: Entry_BE4 -> "BE4 bejárat", SOAPPay = our own API (app / backoffice)
export function gateLabel(gate) {
    const m = /^(Entry|Exit|Payst|Gate)_(.+)$/.exec(gate || '');
    if (m) {
        return `${m[2]} ${
            { Entry: 'bejárat', Exit: 'kijárat', Payst: 'automata', Gate: 'kapu' }[m[1]]
        }`;
    }
    if (gate === 'SOAPPay') return 'App / backoffice';
    return gate || '–';
}

export const gateGroup = (gate) =>
    ({ Entry: 'Bejáratok', Exit: 'Kijáratok', Payst: 'Automaták', Gate: 'Kapuk' }[
        (/^([A-Za-z]+)_/.exec(gate || '') || [])[1]
    ] || (gate === 'SOAPPay' ? 'App / backoffice' : 'Egyéb'));

/**
 * Our view of one event: label, category, severity, whether it is noise
 */
export function describe(e) {
    const t = EVENT_TYPES[e.type];
    let info = t || { label: e.type, cat: 'other', sev: INFO };
    // calls of our own API (getTicketInfo / payTicket of the app and the backoffice) go through the SOAPPay terminal
    // their errors are mostly lookups of a ticket that is closed already (e.g. the backoffice page open after the exit)
    if (e.gate === 'SOAPPay' && info.cat !== 'pay') {
        info = {
            ...info,
            cat: 'app',
            label: `Lekérdezés: ${info.label}`,
            sev: info.sev === FAIL ? WARN : info.sev,
        };
    }
    if (e.type === 'ID moved') {
        info = {
            ...info,
            label: { in: 'Behajtás', out: 'Kihajtás' }[e.dir] || 'Áthaladás',
        };
    }
    if (e.type === 'Ticket info') {
        const fee = e.error || e.fee == null ? 'nem megállapítható' : `${e.fee} Ft`;
        info = {
            ...info,
            label: `Díj a parkoló rendszerben: ${fee}${e.status ? ` (${e.status})` : ''}`,
        };
    }
    if (e.type === 'ID paid' && e.gate === 'SOAPPay')
        info = { ...info, label: 'Parkoló rendszer: fizetés rögzítve (appból)' };
    return info;
}

export const isFailure = (e) => describe(e).sev === FAIL;
export const isProblem = (e) => describe(e).sev !== INFO;

// does the event belong to this ticket / plate (plate, identifier or alias ticket field)
export function matchesTicket(e, id) {
    if (!id) return false;
    const want = String(id).toUpperCase();
    return (
        String(e.plate || '').toUpperCase() === want ||
        e.ident === want ||
        String(e.alias || '')
            .toUpperCase()
            .split(/[\s/]+/)
            .includes(want)
    );
}

// ---- archive (PHP API, per day gzipped NDJSON) ------------------------------------------

const dayCache = new Map(); // date -> Promise<events[]> (closed days are immutable)
const pad = (n) => String(n).padStart(2, '0');
export const dayOf = (ms) => {
    const d = new Date(ms);
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

function token() {
    try {
        return JSON.parse(localStorage.getItem(ACCESS_TOKEN_KEY));
    } catch (e) {
        return null;
    }
}

// the live build has BACKEND_URL with a trailing slash, "//api" would not match any route
const API_BASE = `${String(BACKEND_URL || '').replace(/\/+$/, '')}/api/1.0`;

async function archiveFetch(path) {
    const res = await fetch(`${API_BASE}/admin/parkingevents${path}`, {
        headers: { Authorization: `Bearer ${token()}` },
    });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Esemény archívum: HTTP ${res.status}`);
    return res;
}

export async function fetchArchiveDays() {
    const res = await archiveFetch('');
    return res ? res.json() : { days: [], syncedAt: null };
}

// one day of events (the response is gzipped, the browser unpacks it), [] if the day is not archived
export function fetchArchiveDay(date) {
    const today = dayOf(Date.now());
    if (date !== today && dayCache.has(date)) return dayCache.get(date);
    const p = archiveFetch(`/${date}`).then(async (res) => {
        if (!res) return [];
        const text = await res.text();
        const out = [];
        text.split('\n').forEach((line) => {
            if (!line) return;
            try {
                out.push(JSON.parse(line));
            } catch (e) {
                // half written line, skip
            }
        });
        return out;
    });
    if (date !== today)
        dayCache.set(
            date,
            p.catch(() => dayCache.delete(date) && [])
        );
    return p;
}

/**
 * Events between two timestamps from the archive, deduplicated by key, oldest first
 */
export async function fetchArchiveRange(from, to) {
    const days = [];
    for (let d = new Date(from); d.getTime() <= to; d.setDate(d.getDate() + 1)) {
        days.push(dayOf(d.getTime()));
        d.setHours(0, 0, 0, 0);
    }
    const lists = await Promise.all([...new Set(days)].map(fetchArchiveDay));
    const byKey = new Map();
    lists.flat().forEach((e) => {
        if (e.ts >= from && e.ts <= to) byKey.set(e.k, { ...byKey.get(e.k), ...e });
    });
    return [...byKey.values()].sort((a, b) => a.ts - b.ts);
}

export const LIVE_HOURS = [24, 48, 72];

/**
 * The live view searched for a plate: the live range that still shows the entry, or the archive day of an older one
 */
export function liveParkingUrl(plate, entryTime) {
    const params = new URLSearchParams({ plate });
    const entry = entryTime ? new Date(String(entryTime).replace(' ', 'T')).getTime() : NaN;
    const hours = LIVE_HOURS.find((h) => entry >= Date.now() - h * 3600e3);
    if (entry && !hours) params.set('day', dayOf(entry));
    else params.set('hours', hours || LIVE_HOURS[LIVE_HOURS.length - 1]);
    return `/liveparking?${params}`;
}

// local "YYYY-MM-DD HH:MM:SS", the format the backend sends ticket times in
export const sqlTime = (t) => {
    const d = new Date(t);
    return `${dayOf(t)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const VISIT_DAYS = 14;
const VISIT_BATCH = 4;
const isMove = (e, dir) => e.type === 'ID moved' && e.dir === dir;

/**
 * A plate without a ticket in our DB (it did not park with the app) as a ticket-like object, from the archive:
 * its latest visit, searched newest day first until the entry turns up (at most VISIT_DAYS days back)
 */
export async function findPlateVisit(plate, maxDays = VISIT_DAYS) {
    const { days } = await fetchArchiveDays();
    const dates = days
        .map((d) => d.date)
        .sort()
        .reverse()
        .slice(0, maxDays);
    const byKey = new Map();
    let searched = 0;
    while (searched < dates.length && ![...byKey.values()].some((e) => isMove(e, 'in'))) {
        const batch = dates.slice(searched, searched + VISIT_BATCH);
        // eslint-disable-next-line no-await-in-loop
        const lists = await Promise.all(batch.map(fetchArchiveDay));
        lists.flat().forEach((e) => {
            if (matchesTicket(e, plate)) byKey.set(e.k, { ...byKey.get(e.k), ...e });
        });
        searched += batch.length;
    }
    const events = [...byKey.values()].sort((a, b) => a.ts - b.ts);
    const entry = [...events].reverse().find((e) => isMove(e, 'in')) || null;
    const visit = entry ? events.filter((e) => e.ts >= entry.ts) : events;
    const exit = (entry && visit.find((e) => isMove(e, 'out'))) || null;
    // no entry in the searched days: only the exit of an older visit is known
    const end = exit || (!entry && [...events].reverse().find((e) => isMove(e, 'out'))) || null;
    return {
        id: plate,
        fromEvents: true,
        user_id: null,
        status: (entry && (exit ? 'EXITED' : 'ACTIVE')) || null,
        entryTime: entry ? sqlTime(entry.ts) : null,
        exitTime: end ? sqlTime(end.ts) : null,
        paidAmount: visit
            .filter((e) => e.type === 'ID paid')
            .reduce((s, e) => s + Math.abs(e.amount || 0), 0),
        appliedDiscounts: visit.filter((e) => e.type === 'Discount code used').length,
        kind: (entry || events[events.length - 1] || {}).kind || '',
        eventCount: events.length,
        searchedDays: searched,
    };
}

// ---- visits (PHP API, built from the archive by the cron; the /visits page) -------------

// the API searches at most this many days at once
export const VISIT_MAX_DAYS = 92;

export const VISIT_END = Object.freeze({
    exit: { label: 'Kihajtott', color: 'success' },
    released: { label: 'Kiengedve', color: 'warning' },
    noExit: { label: 'Nincs kihajtás', color: 'error' },
    open: { label: 'Bent van', color: 'info' },
});

export const VISIT_CLS = Object.freeze({
    plate: 'Rendszám',
    ticket: 'Jegy',
    pass: 'Bérlet',
    other: 'Egyéb',
});

// camera image of a passage (uploaded to B2 by the parking proxy), null if it has none
export const imageUrl = (e) => (e && e.img ? `${PARKING_IMG_URL}/${e.img}` : null);

/**
 * Visits by entry day, newest first: {from, to, exact: [...], similar: [{visit, diff}], truncated, missingDays}
 * @param {object} params plate, from, to (YYYY-MM-DD), end, gate, cls, limit
 */
export const searchVisits = (params) => doSwaggerCall('ParkingVisits', 'getParkingVisits', params);

// one visit with its timeline (ev)
export const fetchVisit = (id) => doSwaggerCall('ParkingVisits', 'getParkingVisit', { id });

// a visit's timeline in the event shape of the live feed / the archive (for describe / bewebItem)
export function visitEvents(visit) {
    return (visit.ev || []).map((e) => {
        let dir;
        if (e.type === 'ID moved') {
            if (/^Entry_/.test(e.gate)) dir = 'in';
            else if (/^Exit_/.test(e.gate)) dir = 'out';
        }
        return {
            ...e,
            ts: new Date(e.time.replace(' ', 'T')).getTime(),
            plate: e.plate || visit.plate,
            kind: visit.kind,
            ...(dir ? { dir } : {}),
        };
    });
}

// ---- links (people's decisions about a parking, ParkingLinks API) ------------------------

// what the person checked before linking
export const LINK_BASIS = Object.freeze({
    image: 'Kép egyezik',
    registration: 'Forgalmi bemutatva',
    ticket: 'Jegy bemutatva',
    receipt: 'Fizetési bizonylat',
});

export const LINK_ITEM = Object.freeze({
    plate: 'Rendszámváltozat',
    event: 'Kijárati olvasás',
    visit: 'Másik rekord',
    release: 'Kiengedés',
});

export const RELEASE_TYPE = Object.freeze({
    opening: 'Kézi nyitás',
    exitTicket: '1x kihajtó jegy',
});

// releases of the days that need a person: {suggested: [{release, candidates}], waiting: [{release, reason}], done}
export const fetchLinkQueue = (params) =>
    doSwaggerCall('ParkingLinks', 'getParkingLinkQueue', params);

/**
 * A decision: {action: link|reject|revoke|unidentified, visitId, itemType, itemRef, linkId, basis, note, reasons}
 */
export const saveLink = (body) => doSwaggerCall('ParkingLinks', 'saveParkingLink', {}, body);

// the error text of a failed API call
export const apiError = (e) =>
    (e && e.response && e.response.body && e.response.body.error) || (e && e.message) || String(e);
